import { useCallback, useMemo } from 'react'
import { useStyles } from './styles'
import {
  Button,
  ConexaLogo,
  Text,
  getTheme,
  useMediaQuery,
} from '@conexasaude/conexa-hero'
import { BackStepper } from '../BackStepper'
import { MainLayoutProps } from './types'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { MobileHeader } from '../MobileHeader'
import { RenderIf } from '@/components/RenderIf'
import { Illustration } from '@/components/HideIllustrationsExternal'
import { useParams } from 'react-router-dom'
import { SEND_TO } from '@/modules/Signup/constants'
import CustomTimezone from '@/components/CustomTimezone'
import { getCache } from '@/utils/cache'
import logoConexa from '@/assets/logo-conexa.svg'
import { AnimateEnter } from '@/components/Animate'

export const MainLayout: React.FunctionComponent<MainLayoutProps> = props => {
  const {
    children,
    disabledNextButton,
    actionNextButton,
    variantNextButton,
    variantLinkButton,
    isLoading,
    labelNextButton,
    labelLinkButton,
    actionLinkButton,
    actionBackStepper,
    showSupportButton,
    hideMobileHeader,
    title,
    subtitle,
    showLogo,
    bodySize,
    showfooterCopyrightText = true,
    tagEventName,
    additionalFooterSpacing = false,
    uniqueCode = null,
    showRegisterPsychologistButton = false,
  } = props
  const theme = getTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm})`)
  const isTablet = useMediaQuery(`(min-width: ${theme.grid.breakpoint.lg})`)

  const uniqueCodeCached = getCache<string>('unique-code')

  const styles = useStyles({ bodySize })

  const { logAnalyticsEvent } = useFirebaseContext()
  const params = useParams<{ clinic?: string }>()

  const handleBackStepper = useCallback(() => {
    logAnalyticsEvent('at_patient_activation', {
      interaction: 'back_click',
      options: {
        sendTo: SEND_TO,
      },
    })
    actionBackStepper()
  }, [actionBackStepper, logAnalyticsEvent])

  const showPrivateLogo = useMemo(() => {
    const hasParams = !!params.clinic || !!uniqueCode || !!uniqueCodeCached
    return showLogo && hasParams
  }, [params.clinic, showLogo, uniqueCode, uniqueCodeCached])

  return (
    <div css={styles.layoutContainer}>
      <RenderIf condition={!isMobile}>
        <div css={styles.containerBackground}>
          <AnimateEnter delay={0.2}>
            <div css={styles.backgroundContentContainer}>
              <div css={styles.backgroundNoise} />
              <Illustration
                name="system"
                css={styles.backgroundContainerIcon}
                width={isTablet ? '560px' : '80%'}
              />
              <Text
                variant="headingXLarge"
                color={theme.colors.background.white}
                css={styles.backgroundContainerTitle}
              >
                Saúde digital do jeito que você precisa
              </Text>
              <Text
                variant="headingMedium"
                color={theme.colors.background.white}
                css={styles.backgroundContainerSubtitle}
              >
                Conecte-se com profissionais de saúde e tenha atendimento onde e
                quando precisar.
              </Text>
            </div>
          </AnimateEnter>
        </div>
      </RenderIf>
      <RenderIf condition={isMobile && !hideMobileHeader}>
        <MobileHeader />
      </RenderIf>
      <div css={styles.body}>
        <div css={styles.contentWrapper}>
          <AnimateEnter>
            <RenderIf condition={showLogo && !isMobile}>
              <div css={styles.logoWrapper}>
                <RenderIf condition={showPrivateLogo}>
                  <img
                    src={`${import.meta.env.VITE_APP_LINK_LOGO_AWS}${
                      uniqueCode || params.clinic || uniqueCodeCached
                    }.png`}
                    alt="logo"
                    css={styles.logoWhiteLabel}
                    onError={event => {
                      event.currentTarget.onerror = null
                      event.currentTarget.src = logoConexa
                      event.currentTarget.style.height = '40px'
                    }}
                  />
                </RenderIf>
                <RenderIf condition={!showPrivateLogo}>
                  <ConexaLogo base="colorful" width={150} height={32.35} />
                </RenderIf>
              </div>
            </RenderIf>
            <div css={styles.mobileStepperArea}>
              <RenderIf condition={!!actionBackStepper && isMobile}>
                <BackStepper
                  onActionClick={handleBackStepper}
                  color={theme.colors.neutral[200]}
                />
              </RenderIf>
              <div css={styles.mobileStepperArea}>
                <RenderIf condition={!!actionBackStepper && isMobile}>
                  <BackStepper
                    onActionClick={handleBackStepper}
                    color={theme.colors.neutral[200]}
                  />
                </RenderIf>
              </div>
              <div css={styles.flexContainer}>
                <div css={styles.stepperArea}>
                  <RenderIf condition={!!actionBackStepper && !isMobile}>
                    <BackStepper
                      onActionClick={handleBackStepper}
                      color={theme.colors.neutral[200]}
                    />
                  </RenderIf>
                </div>
                <div css={styles.mainCard}>
                  <div css={styles.contentContainer}>
                    <div css={styles.content}>
                      <RenderIf condition={!!title}>
                        <div css={styles.titleContainer}>
                          <h2 dangerouslySetInnerHTML={{ __html: title }} />
                        </div>
                      </RenderIf>
                      <RenderIf condition={!!subtitle}>
                        <p
                          css={styles.subtitleWrapper}
                          dangerouslySetInnerHTML={{ __html: subtitle }}
                        />
                      </RenderIf>

                      <div css={styles.childrenContainer}>{children}</div>
                      <div css={styles.buttonWrapper}>
                        <RenderIf condition={!!actionNextButton}>
                          <Button
                            disabled={disabledNextButton}
                            onClick={actionNextButton}
                            variant={variantNextButton}
                            busy={isLoading}
                            fullWidth
                          >
                            {labelNextButton}
                          </Button>
                          <RenderIf condition={!!labelLinkButton}>
                            <Button
                              variant={variantLinkButton || 'secondary'}
                              fullWidth
                              onClick={actionLinkButton}
                            >
                              {labelLinkButton}
                            </Button>
                          </RenderIf>
                        </RenderIf>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <CustomTimezone
              showSupportButton={showSupportButton}
              tagEventName={tagEventName}
              tagInteraction="need_help_click"
              supportTextButton="Sou paciente e preciso de ajuda"
              showfooterCopyrightText={showfooterCopyrightText}
              showRegisterPsychologistButton={showRegisterPsychologistButton}
              additionalFooterSpacing={
                (!!actionBackStepper && !isMobile) || additionalFooterSpacing
              }
            />
          </AnimateEnter>
        </div>
      </div>
    </div>
  )
}
